'use client';

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/lib/AuthContext';
import Image from 'next/image';
import styles from '../styles/Login.module.css';

const BANNER_IMAGES = [
  '/images/login_asset.jpg',
  '/images/login_Inventory.jpg',
  '/images/login_smarten_ERP.jpg',
  '/images/registration_Slider_Smarten_ERP.jpg',
  '/images/registration_Slider_Inventory.jpg',
  '/images/registration_Slider_asset.jpg',
];

interface RegisterProps {
    onSwitchToLogin: () => void;
}

export function Register({ onSwitchToLogin }: RegisterProps) {
    const { register } = useAuth();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [imgIndex, setImgIndex] = useState(0);

    useEffect(() => {
        const id = setInterval(() => setImgIndex(i => (i + 1) % BANNER_IMAGES.length), 5000);
        return () => clearInterval(id);
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setIsLoading(true);

        try {
            await register(name, email, password);
        } catch (err: any) {
            setError(err.message || 'Failed to register');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.authCard}>
                {/* LEFT SIDE - REGISTER */}
                <div className={styles.leftPanel}>
                    <div className={styles.loginHeader}>
                        <Image
                            src="/images/logo.svg"
                            alt="Smarten"
                            width={140}
                            height={40}
                            className={styles.logo}
                        />
                        <h1 className={styles.welcomeTitle}>Create Account</h1>
                        <p className={styles.welcomeSubtitle}>
                            Sign up to get started with Smarten Meet
                        </p>
                    </div>

                    {error && (
                        <div className={styles.errorBanner}>
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className={styles.loginForm}>
                        <div className={styles.formGroup}>
                            <label htmlFor="name" className={styles.formLabel}>
                                Full Name
                            </label>
                            <input
                                type="text"
                                id="name"
                                className={styles.formInput}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Enter your name"
                                required
                                disabled={isLoading}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="email" className={styles.formLabel}>
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                className={styles.formInput}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email"
                                required
                                disabled={isLoading}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="password" className={styles.formLabel}>
                                Password
                            </label>
                            <input
                                type="password"
                                id="password"
                                className={styles.formInput}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Create a password"
                                required
                                disabled={isLoading}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="confirmPassword" className={styles.formLabel}>
                                Confirm Password
                            </label>
                            <input
                                type="password"
                                id="confirmPassword"
                                className={styles.formInput}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm your password"
                                required
                                disabled={isLoading}
                            />
                        </div>

                        <button
                            type="submit"
                            className={styles.submitButton}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Creating account...' : 'Sign Up'}
                        </button>
                    </form>

                    <p className={styles.registerPrompt}>
                        Already have an account?{' '}
                        <button
                            type="button"
                            onClick={onSwitchToLogin}
                            className={styles.linkButton}
                            disabled={isLoading}
                        >
                            Sign in
                        </button>
                    </p>
                </div>

                {/* RIGHT SIDE - IMAGE PANEL */}
                <div className={styles.rightPanel}>
                    <Image key={imgIndex} src={BANNER_IMAGES[imgIndex]} alt="Image" fill className={styles.illustration} />
                </div>
            </div>
        </div>
    );
}
